/**
 * Хаб «Агроциклы» (agroai-0e8, замечания владельца 08.10): общая картина по
 * ВСЕМ агроциклам района — идут, предстоящие, закрытые. Карточка цикла —
 * ключевые факты (земля, хозяйства, поля, работы плана, этап) и ход; подробно
 * один цикл — на его странице. Детализации одного цикла здесь нет.
 *
 * Один запрос — `GET /agrocycles` (строки слоя метрик, клиент не складывает);
 * тот же адрес читает страница цикла, поэтому переход в деталь тёплый.
 * Прежние вкладки «Следующая кампания» (сценарный расчёт, живёт на
 * `/planning`) и «История» (сравнение на ту же дату) сняты — журнал №1521.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus } from 'lucide-react';
import { PageHeader } from '../../ui/PageHeader';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { EmptyState } from '../../ui/EmptyState';
import { Skeleton } from '../../ui/Skeleton';
import { LoadError } from '../../ui/LoadError';
import { usePageTrail } from '../../shell/ShellContext';
import { useAuth } from '../../../auth/context';
import { useAnalysisContext } from '../../../analysis/context';
import { useRequest } from '../../../api/useRequest';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { dateShort, windowLabel } from '../../lib/format';
import { agrocycleApi, type AgrocycleRow } from '../agrocycle/agrocycleApi';
import { kpi, num, statusLabel } from '../agrocycle/agrocycleView';
import { CreateCycleDialog } from './CreateCycleDialog';
import { hubSections, planFacts, seasonPosition, type HubSection } from '../cycle/cycleFacts';
import './cyclesHub.css';

const m = defineMessages({
  trailLevel: { id: 'cycle.hub.trail.level', defaultMessage: 'Район', description: 'Хлебная крошка хаба «Агроциклы»: уровень просмотра «Район»' },
  title: { id: 'cycle.hub.title', defaultMessage: 'Агроциклы', description: 'Заголовок и хлебная крошка хаба «Агроциклы»' },
  purpose: {
    id: 'cycle.hub.purpose',
    defaultMessage: 'Все агроциклы района: какие идут, какие впереди и чем закончились прошлые. Откройте цикл — там план, работы и карта.',
    description: 'Подзаголовок хаба «Агроциклы»: назначение экрана одной фразой',
  },
  summary: {
    id: 'cycle.hub.summary',
    defaultMessage: 'идут {started} · предстоящих {upcoming} · закрыто {finished} · срез {date}',
    description: 'Строка-сводка хаба под заголовком: сколько агроциклов в каждом состоянии; числа строкой, {date} — дата среза',
  },
  sectionStarted: { id: 'cycle.hub.section.started', defaultMessage: 'Идут сейчас', description: 'Заголовок раздела хаба: агроциклы, которые идут' },
  sectionUpcoming: { id: 'cycle.hub.section.upcoming', defaultMessage: 'Предстоящие', description: 'Заголовок раздела хаба: агроциклы созданы, но ещё не начаты' },
  sectionFinished: { id: 'cycle.hub.section.finished', defaultMessage: 'Закрытые', description: 'Заголовок раздела хаба: завершённые агроциклы' },
  emptyStarted: { id: 'cycle.hub.empty.started', defaultMessage: 'Сейчас ни один агроцикл не идёт.', description: 'Пустой раздел «Идут сейчас»' },
  emptyUpcoming: {
    id: 'cycle.hub.empty.upcoming',
    defaultMessage: 'Предстоящих агроциклов нет. Менеджер создаёт цикл по культуре и году посадки.',
    description: 'Пустой раздел «Предстоящие»: как появляется предстоящий агроцикл',
  },
  emptyFinished: { id: 'cycle.hub.empty.finished', defaultMessage: 'Закрытых агроциклов пока нет.', description: 'Пустой раздел «Закрытые»' },
  emptyAll: { id: 'cycle.hub.empty.all', defaultMessage: 'Агроциклов в районе пока нет', description: 'Пустое состояние хаба: ни одного агроцикла' },
  loadWhat: { id: 'cycle.hub.loadWhat', defaultMessage: 'агроциклы района', description: 'Что не загрузилось (винительный падеж, в «Не удалось загрузить …»)' },
  factLand: { id: 'cycle.hub.fact.land', defaultMessage: 'земли', description: 'Подпись факта карточки агроцикла под числом гектаров: уникальная земля цикла' },
  factFarms: { id: 'cycle.hub.fact.farms', defaultMessage: 'хозяйств', description: 'Подпись факта карточки агроцикла под числом: хозяйства-участники' },
  factFields: { id: 'cycle.hub.fact.fields', defaultMessage: 'полей', description: 'Подпись факта карточки агроцикла под числом: поля цикла по размещению' },
  factWorks: { id: 'cycle.hub.fact.works', defaultMessage: 'работ запущено', description: 'Подпись факта карточки агроцикла под «38 из 40»: запущено работ плана' },
  planLine: {
    id: 'cycle.hub.planLine',
    defaultMessage: 'впереди {ahead, plural, one {# работа плана} few {# работы плана} many {# работ плана} other {# работы плана}}',
    description: 'Строка под фактами карточки агроцикла: сколько работ плана ещё не запущено; {ahead} — число',
  },
  planNone: { id: 'cycle.hub.planNone', defaultMessage: 'план не посчитан', description: 'Строка карточки агроцикла: сервер не посчитал план' },
  ofTotal: { id: 'cycle.hub.ofTotal', defaultMessage: '{launched} из {total}', description: 'Факт карточки: запущено K из M работ плана; числа строкой' },
  stage: { id: 'cycle.hub.stage', defaultMessage: 'этап: {name}', description: 'Текущий этап агроцикла на карточке хаба; {name} — название этапа' },
  stageNone: { id: 'cycle.hub.stageNone', defaultMessage: 'этап не начат', description: 'Карточка агроцикла: текущего этапа нет (цикл не начат или между этапами)' },
  progress: {
    id: 'cycle.hub.progress',
    defaultMessage: 'выполнено {area} % площади работ запущенных кампаний · {count} % по числу',
    description: 'Ход агроцикла на карточке хаба: доля выполненного по площади работ и по числу работ; основа — работы запущенных кампаний плана; числа строкой',
  },
  progressFinal: {
    id: 'cycle.hub.progressFinal',
    defaultMessage: 'итог: выполнено {area} % площади работ · {count} % по числу',
    description: 'Итог закрытого агроцикла на карточке хаба: доля выполненного по площади и по числу работ; числа строкой',
  },
  progressNone: { id: 'cycle.hub.progressNone', defaultMessage: 'кампаний по плану ещё не запускали', description: 'Ход агроцикла на карточке: по плану ещё нет запущенных кампаний' },
  season: { id: 'cycle.hub.season', defaultMessage: 'окно агроцикла пройдено на {pct} %', description: 'Подпись под полосой окна агроцикла на карточке хаба: сколько окна прошло к дате среза; {pct} — число' },
  finished: { id: 'cycle.hub.finished', defaultMessage: 'закрыт {date}', description: 'Карточка закрытого агроцикла: дата завершения; {date} — дата' },
  starts: { id: 'cycle.hub.starts', defaultMessage: 'начало {date}', description: 'Карточка предстоящего агроцикла: дата начала по плану; {date} — дата' },
  open: { id: 'cycle.hub.open', defaultMessage: 'Открыть агроцикл', description: 'Ссылка с карточки хаба на страницу агроцикла' },
  create: { id: 'cycle.hub.create.button', defaultMessage: 'Создать агроцикл', description: 'Кнопка в шапке хаба (менеджер): открывает окно с выбором культуры, года и шаблона' },
});

const SECTIONS: { key: HubSection; title: PlainMessage; empty: PlainMessage }[] = [
  { key: 'started', title: m.sectionStarted, empty: m.emptyStarted },
  { key: 'not_started', title: m.sectionUpcoming, empty: m.emptyUpcoming },
  { key: 'finished', title: m.sectionFinished, empty: m.emptyFinished },
];

type Params = Record<string, string | number | boolean | null | undefined>;

function CycleCard({ row, horizon }: { row: AgrocycleRow; horizon: string }) {
  const t = useT();
  const k = row.kpis;
  const facts = planFacts(row, horizon);
  const area = kpi(k, 'area.done_pct');
  const count = kpi(k, 'reports.done_pct');
  const pos = row.status === 'finished' ? 1 : row.status === 'not_started' ? 0 : seasonPosition(row.plan_from, row.plan_to, horizon);
  const tone = row.status === 'started' ? 'green' : row.status === 'finished' ? 'outline' : 'neutral';
  const progressText = area === null && count === null
    ? t(m.progressNone)
    : t(row.status === 'finished' ? m.progressFinal : m.progress, { area: num(area), count: num(count) });
  return (
    <Link className="hub-card focus-ring" data-status={row.status} to={`/cycles/${encodeURIComponent(row.key)}`}>
      <span className="hub-card__head">
        <strong className="hub-card__name">{row.name}</strong>
        <Badge tone={tone}>{statusLabel(row.status)}</Badge>
      </span>
      <span className="hub-card__window">
        {row.plan_from && row.plan_to ? windowLabel(row.plan_from, row.plan_to) : '—'}
        {' · '}
        {row.status === 'not_started'
          ? t(m.starts, { date: dateShort(row.plan_from) })
          : row.status === 'finished'
            ? t(m.finished, { date: dateShort(row.finished_on ?? row.plan_to) })
            : row.current_stage ? t(m.stage, { name: row.current_stage.name }) : t(m.stageNone)}
      </span>
      {pos === null || row.status !== 'started' ? null : (
        <span className="hub-season">
          <span className="hub-season__bar" aria-hidden="true"><i style={{ width: `${pos * 100}%` }} /></span>
          <small>{t(m.season, { pct: num(Math.round(pos * 100)) })}</small>
        </span>
      )}
      <span className="hub-facts">
        <span className="hub-fact"><b className="num">{num(kpi(k, 'cycle.land_ha'))}</b><small>{t(UNIT_LABELS.ha)} {t(m.factLand)}</small></span>
        <span className="hub-fact"><b className="num">{num(kpi(k, 'cycle.farms'))}</b><small>{t(m.factFarms)}</small></span>
        <span className="hub-fact"><b className="num">{num(kpi(k, 'cycle.fields'))}</b><small>{t(m.factFields)}</small></span>
        <span className="hub-fact">
          <b className="num">{facts.total === null ? '—' : t(m.ofTotal, { launched: num(facts.launched), total: num(facts.total) })}</b>
          <small>{t(m.factWorks)}</small>
        </span>
      </span>
      <span className="hub-card__plan">
        {facts.ahead === null ? t(m.planNone) : facts.ahead > 0 ? t(m.planLine, { ahead: facts.ahead }) : null}
      </span>
      {row.status === 'not_started' ? null : (
        <span className="hub-progress">
          <span className="hub-bar" aria-hidden="true"><i style={{ width: `${Math.min(100, area ?? 0)}%` }} /></span>
          <span className="hub-progress__text">{progressText}</span>
        </span>
      )}
      <span className="hub-card__open">{t(m.open)} <ArrowRight size={14} aria-hidden="true" /></span>
    </Link>
  );
}

function HubSkeleton() {
  return (
    <div className="hub-grid" aria-hidden="true">
      {[0, 1, 2].map((i) => <Skeleton key={i} variant="block" height={236} />)}
    </div>
  );
}

export function CyclesHub() {
  const t = useT();
  usePageTrail([{ level: t(m.trailLevel), name: t(m.title) }]);
  const { params } = useAnalysisContext();
  const canEdit = useAuth().principal?.can_edit_agrocycle ?? false;
  const req = useRequest(() => agrocycleApi.list(params as Params), [JSON.stringify(params)]);
  const rows = req.data?.items ?? [];
  const horizon = req.data?.horizon ?? '';
  const sections = hubSections(rows);
  const [creating, setCreating] = useState(false);

  // Разделы — отдельными элементами с явными метками: по ним ИИ-гид находит блок.
  const renderSection = (section: (typeof SECTIONS)[number]) => (
    <>
      <h2 className="hub-section__title">
        {t(section.title)} <span className="hub-section__count num">{sections[section.key].length}</span>
      </h2>
      {sections[section.key].length === 0 ? (
        <Card tone="soft"><p className="hub-muted">{t(section.empty)}</p></Card>
      ) : (
        <div className="hub-grid">
          {sections[section.key].map((row) => <CycleCard key={row.key} row={row} horizon={horizon} />)}
        </div>
      )}
    </>
  );

  return (
    <div className="hub">
      <PageHeader
        level="district"
        title={t(m.title)}
        purpose={t(m.purpose)}
        actions={
          canEdit ? (
            <button type="button" className="astra-button astra-button-primary" data-guide="hub-create" onClick={() => setCreating(true)}>
              <Plus size={16} /> {t(m.create)}
            </button>
          ) : null
        }
      >
        {req.data ? (
          <p className="hub-summary num" data-guide="hub-summary">
            {t(m.summary, {
              started: num(sections.started.length),
              upcoming: num(sections.not_started.length),
              finished: num(sections.finished.length),
              date: dateShort(horizon),
            })}
          </p>
        ) : null}
      </PageHeader>
      {req.error && !req.data ? (
        <LoadError what={t(m.loadWhat)} error={req.error} onRetry={req.reload} />
      ) : !req.data ? (
        <HubSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState title={t(m.emptyAll)} reason={t(m.emptyUpcoming)} />
      ) : (
        <>
          <section className="hub-section" data-guide="hub-started">{renderSection(SECTIONS[0]!)}</section>
          <section className="hub-section" data-guide="hub-upcoming">{renderSection(SECTIONS[1]!)}</section>
          <section className="hub-section" data-guide="hub-finished">{renderSection(SECTIONS[2]!)}</section>
        </>
      )}
      {creating ? <CreateCycleDialog params={params as Params} rows={rows} onClose={() => setCreating(false)} /> : null}
    </div>
  );
}
